import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Editor, EditorModule } from '@openng/optimus-ui/editor';
import type Quill from 'quill';
import type { Delta } from 'quill';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../directives/base-form-control.directive';
import {
  RichTextEditorFormControlDirective,
  RichTextEditorFormControlDirectiveInputs,
} from '../directives/rich-text-editor-form-control.directive';

import { FormComponent } from '../form/form.component';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { uniqueId } from '@shared/utils';

/**
 * Povolené formáty = jen to, co nabízí lišta (#header v šabloně – při změně lišty upravit i zde). Ostatní (obrázek,
 * video, vzorec, odkaz…) editor nepřijme ani při vložení ze schránky; export do HTML (getSemanticHTML) u nich
 * neescapuje hodnoty (CVE-2025-15056).
 */
const ALLOWED_FORMATS = ['size', 'bold', 'italic', 'list'];

@Component({
  selector: 'ui-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  imports: [EditorModule, ReactiveFormsModule, InputWrapperComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RichTextEditorComponent,
      multi: true,
    },
  ],
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: RichTextEditorFormControlDirective,
      inputs: RichTextEditorFormControlDirectiveInputs,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorComponent extends BaseControlValueAccessor<string> implements AfterViewInit {
  private _form = inject(FormComponent, { optional: true, skipSelf: true });
  submitted = false;
  readonly editorComponent = viewChild<Editor>('nativeInputElement');
  readonly editorElementRef = viewChild('nativeInputElement', { read: ElementRef });
  observer!: MutationObserver;
  quill: any;
  characterCount = 0; // Přidáme proměnnou pro sledování počtu znaků

  base = inject(BaseFormControlDirective);
  rich = inject(RichTextEditorFormControlDirective);
  readonly cd = inject(ChangeDetectorRef);

  randomInputId = uniqueId();
  private initialValue = '';

  readonly formats = ALLOWED_FORMATS;

  /** Dočasný editor pro měření délky HTML při ořezu na maxLength; vzniká až při prvním použití. */
  private tempQuill?: Quill;

  constructor() {
    const injector = inject(Injector);

    super(injector);

    if (this._form) {
      this._form
        .getSubmitted()
        .pipe(takeUntilDestroyed())
        .subscribe((_) => (this.submitted = _));
    }
  }

  ngAfterViewInit() {
    this.observer = new MutationObserver(() => {
      this.quill = this.editorComponent()?.quill;
      if (this.quill) {
        // Track paste events
        this.quill.root.addEventListener('paste', () => {
          this.quill.root.style.visibility = 'hidden';
          setTimeout(() => {
            const sanitized = this.sanitizeHtml(this.quill.root.innerHTML);
            if (this.quill.root.innerHTML !== sanitized) {
              const cursorPos = this.quill.getSelection()?.index || 0;

              // Update Quill content with sanitized HTML
              this.quill.clipboard.dangerouslyPasteHTML(sanitized, 'api');
              this.quill.setSelection(Math.min(cursorPos, this.quill.getLength() - 1));
            }
            this.quill.root.style.visibility = 'visible';
          }, 0); // delay so Quill finishes its own paste first
        });

        if (this.initialValue != null) {
          this.quill.clipboard.dangerouslyPasteHTML(this.initialValue);
        }
        // Aktualizujeme počet znaků při načtení komponenty
        this.updateValue(this.quill);

        // Jakmile je instance Quill načtena, přidáme event listener a odpojíme observer
        this.quill.on('text-change', (delta: Delta, oldDelta: Delta, source: string) => {
          if (source === 'user') {
            const maxLength = this.rich.maxLength();
            if (maxLength) {
              const currentCursorPosition = this.quill.getSelection()?.index || 0; // Uložení pozice kurzoru

              const truncatedDelta = this.truncateDeltaByHtmlLength(this.quill, maxLength);
              if (truncatedDelta) {
                this.quill.setContents(truncatedDelta, 'api'); // Nastavení obsahu
                this.quill.setSelection(Math.min(currentCursorPosition, this.quill.getLength() - 1)); // Obnovení pozice kurzoru
              }
            }
          }
          this.updateValue(this.quill);
        });
        this.observer.disconnect();
      }
    });

    // Sleduje změny v nativním elementu editoru pro detekci načtení Quill instance
    const editorElementRef = this.editorElementRef();
    if (editorElementRef?.nativeElement) {
      this.observer.observe(editorElementRef.nativeElement, { childList: true, subtree: true });
    }
  }

  // #region -------------------------------------------------------- ControlValueAccessor --------------------------------------------------------

  // Prázdné funkce, které se zaregistrují pro změny hodnoty a dotyk
  // ted se nepozivaji, je reseno pomoci formControlDirective
  // onChange: (value: DateRange | null) => void = noop;
  // onTouched: () => void = noop;

  // Tato metoda je volána API formulářů pro zápis do zobrazení, když jsou požadovány programové změny z modelu do zobrazení
  override writeValue(value: string): void {
    this.value.set(value);
    if (this.quill && value != null) {
      setTimeout(() => {
        this.quill.clipboard.dangerouslyPasteHTML(value);
      }, 0);
    } else {
      this.initialValue = value;
    }
  }
  // #endregion -------------------------------------------------------- ControlValueAccessor --------------------------------------------------------

  // Metoda pro aktualizaci hodnoty a počtu znaků
  private updateValue(quill: any): void {
    let htmlContent = quill.root.innerHTML; // Získáme pouze textový obsah bez HTML tagů
    htmlContent = this.sanitizeHtml(htmlContent); // sanitize pasted HTML

    // Update the quill content if sanitization removed anything
    if (quill.root.innerHTML !== htmlContent) {
      const cursorPos = quill.getSelection()?.index || 0;
      quill.setSelection(Math.min(cursorPos, quill.getLength() - 1));
    }

    // Získáme html zdroj, tzn. včetně HTML tagů
    this.characterCount = htmlContent.length;
    this.onChange(htmlContent);
    this.cd.detectChanges();
  }

  private truncateDeltaByHtmlLength(quill: any, maxLength: number): Delta | null {
    // Třída Quill z instance editoru: Optimus UI načítá Quill dynamicky až s editorem, statický import by ho
    // přidal do úvodního balíčku aplikace.
    const QuillCtor = quill.constructor as typeof Quill;
    const DeltaCtor = QuillCtor.import('delta') as typeof Delta;
    const tempQuill = (this.tempQuill ??= new QuillCtor(document.createElement('div'), { formats: this.formats }));
    const originalDelta = quill.getContents();
    const truncatedDelta = new DeltaCtor();

    for (const op of originalDelta.ops) {
      truncatedDelta.push(op); // Přidáme aktuální operaci do oříznutého obsahu

      // Dočasný editor pro měření délky HTML
      tempQuill.setContents(truncatedDelta);

      // Zjistíme délku HTML z dočasného editoru
      const htmlLength = tempQuill.root.innerHTML.length;

      // Pokud délka HTML překročí povolený limit
      if (htmlLength > maxLength) {
        // Odstraníme poslední přidanou operaci
        const lastOp = truncatedDelta.ops.pop();

        // Pokud poslední operace obsahuje text, zkrátíme ji
        if (lastOp && typeof lastOp.insert === 'string') {
          const excessLength = htmlLength - maxLength;
          const allowedLength = lastOp.insert.length - excessLength; // Zkrátíme přesně o přebývající délku
          truncatedDelta.push({
            insert: lastOp.insert.slice(0, allowedLength - 1),
            attributes: lastOp.attributes,
          });
        } else if (lastOp) {
          // Pokud poslední operace není text, přidáme ji celou zpět
          truncatedDelta.push(lastOp);
        }

        // Přesná kontrola délky před vrácením
        tempQuill.setContents(truncatedDelta);
        if (tempQuill.root.innerHTML.length > maxLength) {
          // V případě, že délka stále přesahuje, odstraníme další část textu
          truncatedDelta.ops.pop();
        }

        return truncatedDelta;
      }
    }

    // Pokud délka HTML nikdy nepřesáhla limit, vracíme null
    return null;
  }

  private sanitizeHtml(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;

    const cleanNode = (node: HTMLElement) => {
      const allowedTags = ['P', 'STRONG', 'EM', 'OL', 'UL', 'LI', 'SPAN', 'BR'];
      const allowedSpanClasses = ['ql-size-small', 'ql-size-large', 'ql-size-huge'];

      // If tag not allowed, unwrap it
      if (!allowedTags.includes(node.tagName)) {
        node.replaceWith(...Array.from(node.childNodes));
        return;
      }

      // Convert <ol data-list="bullet"> to <ul>
      if (
        node.tagName === 'OL' &&
        Array.from(node.childNodes).some(
          (child) =>
            child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).getAttribute('data-list') === 'bullet'
        )
      ) {
        const ul = document.createElement('ul');
        ul.innerHTML = node.innerHTML;
        node.replaceWith(ul);
        // Recursively sanitize the new UL immediately
        cleanNode(ul);
        return; // stop processing this node, we already sanitized the replacement
      }

      if (node.tagName === 'LI' && node.getAttribute('data-list') === 'bullet') {
        node.removeAttribute('data-list');
      }

      if (['SPAN', 'STRONG', 'EM'].includes(node.tagName)) {
        // Keep only allowed classes
        const classes = Array.from(node.classList).filter((c) => allowedSpanClasses.includes(c));
        node.className = classes.join(' ');

        // Remove inline styles
        node.removeAttribute('style');
      } else {
        // Remove all attributes except for LI's data-list (needed for Quill lists)
        Array.from(node.attributes).forEach((attr) => {
          if (!(node.tagName === 'LI' && attr.name === 'data-list')) {
            node.removeAttribute(attr.name);
          }
        });
      }

      // Recursively sanitize children
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          cleanNode(child as HTMLElement);
        }
      });
    };

    Array.from(container.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) cleanNode(node as HTMLElement);
    });

    this.normalizeMixedLists(container);

    return container.innerHTML;
  }

  private normalizeMixedLists(container: HTMLElement): void {
    const lists = Array.from(container.querySelectorAll('ul')).filter((ul) => !ul.querySelector('ul, ol'));

    lists.forEach((ul) => {
      const items = Array.from(ul.children) as HTMLElement[];

      if (!items.length) return;

      const resultFragments: HTMLElement[] = [];

      let currentList: HTMLElement | null = null;
      let currentType: 'ul' | 'ol' | null = null;

      const createList = (type: 'ul' | 'ol') => {
        const el = document.createElement(type);
        el.style.margin = '0';
        return el;
      };

      items.forEach((li) => {
        const isOrdered = li.getAttribute('data-list') === 'ordered';
        const type: 'ul' | 'ol' = isOrdered ? 'ol' : 'ul';

        if (currentType !== type) {
          currentList = createList(type);
          resultFragments.push(currentList);
          currentType = type;
        }

        const cleanLi = li.cloneNode(true) as HTMLElement;
        cleanLi.removeAttribute('data-list');

        currentList!.appendChild(cleanLi);
      });

      // Replace original UL
      resultFragments.forEach((list) => {
        ul.parentNode?.insertBefore(list, ul);
      });

      ul.remove();
    });
  }
}
